import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUp, ArrowUpRight } from 'lucide-react';
import { IMG, LOGO_URL, PROCESS, CLINIC } from './dionData';
import { Reveal, ThinLine } from './DionShared';

/* SCENE 11 — PROCESS */
export const ProcessScene = () => (
  <section className="relative bg-[#07090e] py-24 lg:py-32" data-testid="dion-process">
    <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
      <Reveal className="mb-4"><span className="dh-eyebrow">11 — Ablauf</span></Reveal>
      {PROCESS.map((step, i) => (
        <div key={step.n} className={`grid items-center gap-8 border-t border-white/10 py-14 lg:min-h-[70vh] lg:grid-cols-2 lg:gap-16 ${i % 2 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
          <Reveal>
            <div className="flex items-baseline gap-6">
              <span className="dh-display text-[clamp(3.5rem,11vw,9rem)] leading-none text-[#5b8da8]/25">{step.n}</span>
              <h3 className="dh-display text-[clamp(1.8rem,4.5vw,3rem)] text-white">{step.title}</h3>
            </div>
            <ThinLine width="60px" className="mt-6" />
            <p className="mt-6 max-w-md text-sm leading-relaxed text-white/70">{step.text}</p>
          </Reveal>
          <Reveal delay={0.12} className="aspect-[16/11] overflow-hidden bg-[#111827]">
            <img src={step.image} alt={`${step.title} — Dion Hair Clinic`} className="dh-media-img" loading="lazy" />
          </Reveal>
        </div>
      ))}
    </div>
  </section>
);

/* SCENE 12 — FINAL BRAND MOMENT */
export const FinalScene = ({ onConsult }) => {
  const reduced = useReducedMotion();
  return (
    <section id="final" className="relative flex h-[100svh] min-h-[620px] items-center overflow-hidden" data-testid="dion-final">
      <motion.div className="absolute inset-0" initial={reduced ? false : { scale: 1.1 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}>
        <img src={IMG.final} alt="Symbolisches Abschlussporträt" className="dh-media-img" />
      </motion.div>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(10,13,20,.94), rgba(10,13,20,.5) 55%, rgba(10,13,20,.75))' }} />
      <div className="relative mx-auto w-full max-w-[1600px] px-5 text-center sm:px-8">
        <Reveal>
          <h2 className="dh-display text-[clamp(2.4rem,7vw,5.5rem)] text-white">
            NATÜRLICHKEIT.<br /><span className="text-white/55">BIS INS DETAIL.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2} className="mt-10 flex flex-col items-center">
          <motion.img src={LOGO_URL} alt="Dion Hair Clinic" width="150" height="90" className="dh-logo h-12 w-auto"
            initial={reduced ? false : { opacity: 0, y: 12 }} whileInView={{ opacity: 0.95, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5, duration: 1.2 }} />
          <span className="dh-kicker mt-4 text-white/60">Dion Hair Clinic</span>
        </Reveal>
        <Reveal delay={0.35} className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <button onClick={onConsult} className="dh-btn dh-btn-solid" data-testid="final-cta-consultation">
            Persönliche Beratung vereinbaren <ArrowUpRight size={16} />
          </button>
          <Link to="/#kontakt" className="dh-btn dh-btn-ghost" data-testid="final-cta-analysis">
            Haaranalyse starten <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

/* FOOTER */
export const DionFooter = ({ onTop }) => (
  <footer className="relative border-t border-white/10 bg-[#07090e] py-16" data-testid="dion-footer">
    <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <img src={LOGO_URL} alt="Dion Hair Clinic" width="120" height="72" className="dh-logo h-9 w-auto opacity-90" />
          <p className="dh-kicker mt-5 leading-relaxed text-white/40">Präzision. Planung. Natürlichkeit.</p>
        </div>
        <div>
          <h3 className="dh-kicker text-white/50">Kontakt</h3>
          <a href={`tel:${CLINIC.phone.replace(/\s/g, '')}`} className="mt-4 block text-sm text-white/80 hover:text-white" data-testid="dion-footer-phone">{CLINIC.phone}</a>
          <a href={`mailto:${CLINIC.email}`} className="mt-2 block text-sm text-white/80 hover:text-white" data-testid="dion-footer-email">{CLINIC.email}</a>
        </div>
        <div>
          <h3 className="dh-kicker text-white/50">Besuchen Sie uns</h3>
          <address className="mt-4 text-sm not-italic leading-relaxed text-white/70">{CLINIC.street}<br />{CLINIC.city}</address>
        </div>
        <div>
          <h3 className="dh-kicker text-white/50">Rechtliches</h3>
          <div className="mt-4 space-y-2">
            <Link to="/impressum" className="block text-sm text-white/70 hover:text-white" data-testid="dion-footer-impressum">Impressum</Link>
            <Link to="/datenschutz" className="block text-sm text-white/70 hover:text-white" data-testid="dion-footer-datenschutz">Datenschutz</Link>
            <Link to="/agb" className="block text-sm text-white/70 hover:text-white" data-testid="dion-footer-agb">AGB</Link>
          </div>
        </div>
      </div>
      <div className="mt-14 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6">
        <p className="dh-kicker text-white/35">© {new Date().getFullYear()} Dion Hair Clinic · Konzept-/Designstudie · Bilder teils symbolisch</p>
        <button onClick={onTop} className="dh-textlink" data-testid="dion-back-to-top">Nach oben <ArrowUp size={15} /></button>
      </div>
    </div>
  </footer>
);
